/**
 * AI 중복검출 dedup 상태 저장 — report별 localStorage.
 * "삭제"가 아니라 seq 구분(숨김 = duplicate)만 기록하므로 언제든 되돌릴 수 있다.
 * 지도는 이 상태의 뷰: hiddenSeqs에 있는 마커만 스타일을 비워 숨긴다.
 */

const STORAGE_KEY_PREFIX = 'solvit.aiDefectDedup';

/**
 * @param {string|number|null|undefined} reportId
 * @returns {string|null}
 */
export function aiDefectDedupStorageKey(reportId) {
  if (reportId == null || reportId === '') return null;
  const id = String(reportId).trim();
  if (!id) return null;
  return `${STORAGE_KEY_PREFIX}.${id}`;
}

/**
 * @typedef {Object} AiDefectDedupDraft 검토 진행 초안 — 적용 전 자동 저장분
 * @property {number} thresholdMeters draft가 만들어진 임계값 (불일치 시 폐기)
 * @property {number} groupIndex 마지막으로 보던 그룹 인덱스
 * @property {string[]} visitedGroupIds 검토(표시)한 그룹 id 목록
 * @property {Record<string, number[]>} keepSeqsByGroupId 그룹별 남길 seq 목록
 */

/**
 * @typedef {Object} AiDefectDedupState
 * @property {number[]} hiddenSeqs 중복으로 구분되어 지도에서 숨길 seq 목록 (적용분)
 * @property {number|null} thresholdMeters 그룹핑에 쓴 거리 임계값
 * @property {string|null} updatedAt ISO timestamp
 * @property {AiDefectDedupDraft|null} draft
 */

/** @returns {AiDefectDedupState} */
export function emptyAiDefectDedupState() {
  return { hiddenSeqs: [], thresholdMeters: null, updatedAt: null, draft: null };
}

/** @returns {AiDefectDedupDraft|null} */
function parseDraft(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const threshold = Number(raw.thresholdMeters);
  if (!Number.isFinite(threshold) || threshold <= 0) return null;
  const keepSeqsByGroupId = {};
  if (raw.keepSeqsByGroupId && typeof raw.keepSeqsByGroupId === 'object') {
    Object.keys(raw.keepSeqsByGroupId).forEach(groupId => {
      const seqs = raw.keepSeqsByGroupId[groupId];
      if (!Array.isArray(seqs)) return;
      // 빈 배열 유지 — "그룹 전체 숨김(결함 아님)" 상태
      keepSeqsByGroupId[groupId] = seqs
        .map(Number)
        .filter(n => Number.isFinite(n) && n > 0);
    });
  }
  const idx = Number(raw.groupIndex);
  return {
    thresholdMeters: threshold,
    groupIndex: Number.isFinite(idx) && idx >= 0 ? idx : 0,
    visitedGroupIds: Array.isArray(raw.visitedGroupIds)
      ? raw.visitedGroupIds.filter(id => typeof id === 'string' && id)
      : [],
    keepSeqsByGroupId
  };
}

/**
 * @param {string|number|null|undefined} reportId
 * @returns {AiDefectDedupState}
 */
export function loadAiDefectDedupState(reportId) {
  const key = aiDefectDedupStorageKey(reportId);
  if (!key) return emptyAiDefectDedupState();
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return emptyAiDefectDedupState();
    const parsed = JSON.parse(raw);
    const hiddenSeqs = Array.isArray(parsed?.hiddenSeqs)
      ? parsed.hiddenSeqs.map(Number).filter(n => Number.isFinite(n) && n > 0)
      : [];
    const t = Number(parsed?.thresholdMeters);
    return {
      hiddenSeqs,
      thresholdMeters: Number.isFinite(t) && t > 0 ? t : null,
      updatedAt: typeof parsed?.updatedAt === 'string' ? parsed.updatedAt : null,
      draft: parseDraft(parsed?.draft)
    };
  } catch (e) {
    console.warn('[aiDefectDedupStore] load failed', e);
    return emptyAiDefectDedupState();
  }
}

/**
 * 전체 상태 저장. draft를 넘기지 않으면 기존 draft를 유지한다.
 * @param {string|number} reportId
 * @param {{ hiddenSeqs: number[], thresholdMeters?: number|null, draft?: AiDefectDedupDraft|null }} state
 */
export function saveAiDefectDedupState(reportId, state) {
  const key = aiDefectDedupStorageKey(reportId);
  if (!key) return;
  try {
    const hiddenSeqs = Array.from(
      new Set(
        (state?.hiddenSeqs || [])
          .map(Number)
          .filter(n => Number.isFinite(n) && n > 0)
      )
    );
    const prevDraft =
      state && 'draft' in state ? state.draft : loadAiDefectDedupState(reportId).draft;
    localStorage.setItem(
      key,
      JSON.stringify({
        hiddenSeqs,
        thresholdMeters: state?.thresholdMeters ?? null,
        updatedAt: new Date().toISOString(),
        draft: prevDraft ?? null
      })
    );
  } catch (e) {
    console.warn('[aiDefectDedupStore] save failed', e);
  }
}

/**
 * 검토 진행 초안만 갱신 — 적용된 hiddenSeqs는 그대로 둔다.
 * @param {string|number} reportId
 * @param {AiDefectDedupDraft|null} draft
 */
export function saveAiDefectDedupDraft(reportId, draft) {
  const key = aiDefectDedupStorageKey(reportId);
  if (!key) return;
  try {
    const current = loadAiDefectDedupState(reportId);
    localStorage.setItem(
      key,
      JSON.stringify({
        hiddenSeqs: current.hiddenSeqs,
        thresholdMeters: current.thresholdMeters,
        updatedAt: new Date().toISOString(),
        draft: draft ?? null
      })
    );
  } catch (e) {
    console.warn('[aiDefectDedupStore] save draft failed', e);
  }
}

/** @param {string|number|null|undefined} reportId */
export function clearAiDefectDedupState(reportId) {
  const key = aiDefectDedupStorageKey(reportId);
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.warn('[aiDefectDedupStore] clear failed', e);
  }
}
